/** @param {string} value */
export const normalizeForSearch = (value) => {
  if (typeof value !== 'string') return ''
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * Resolves the normalized uppercase A-Z bucket for a brand title.
 *
 * @param {string} title
 * @returns {string}
 */
export const resolveLetterFromTitle = (title) => {
  if (typeof title !== 'string') return '#'
  const normalized = title
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim()
    .toUpperCase()
  const first = normalized.charAt(0)
  return /^[A-Z]$/.test(first) ? first : '#'
}

/**
 * Formats the localized brand count label.
 *
 * @param {number} count
 * @param {string} [singular]
 * @param {string} [plural]
 * @returns {string}
 */
export const formatBrandCountLabel = (
  count,
  singular = 'marque',
  plural = 'marques'
) => {
  const safeCount = Number.isFinite(count) && count >= 0 ? Math.trunc(count) : 0
  return `${safeCount} ${safeCount === 1 ? singular : plural}`
}

/**
 * Partitions ordered letter groups into `columnCount` contiguous alphabetical
 * columns balanced by estimated card height (header 56px + 48px per row + 18px gap).
 *
 * @template {{ letter: string, itemCount: number }} T
 * @param {T[]} letterGroups
 * @param {number} columnCount
 * @returns {T[][]}
 */
export const distributeLettersIntoColumns = (letterGroups, columnCount) => {
  const groups = Array.isArray(letterGroups) ? letterGroups : []
  const k = Math.max(1, Math.trunc(columnCount || 1))

  if (k === 1 || groups.length === 0) {
    return [groups.slice()]
  }

  const n = groups.length
  if (n <= k) {
    return Array.from({ length: k }, (_, idx) => (idx < n ? [groups[idx]] : []))
  }

  const weights = groups.map(
    (group) => 56 + Math.max(1, group.itemCount) * 48 + 18
  )
  const prefix = new Array(n + 1).fill(0)
  for (let i = 0; i < n; i += 1) {
    prefix[i + 1] = prefix[i] + weights[i]
  }

  const rangeSum = (start, endExclusive) => prefix[endExclusive] - prefix[start]

  /** @type {number[][]} */
  const dp = Array.from({ length: k + 1 }, () =>
    new Array(n + 1).fill(Number.POSITIVE_INFINITY)
  )
  /** @type {number[][]} */
  const splitAt = Array.from({ length: k + 1 }, () => new Array(n + 1).fill(0))

  for (let i = 1; i <= n; i += 1) {
    dp[1][i] = rangeSum(0, i)
  }

  for (let cols = 2; cols <= k; cols += 1) {
    for (let i = cols; i <= n; i += 1) {
      for (let prev = cols - 1; prev < i; prev += 1) {
        const candidate = Math.max(dp[cols - 1][prev], rangeSum(prev, i))
        if (candidate < dp[cols][i]) {
          dp[cols][i] = candidate
          splitAt[cols][i] = prev
        }
      }
    }
  }

  /** @type {T[][]} */
  const columns = new Array(k)
  let end = n
  for (let cols = k; cols >= 1; cols -= 1) {
    const start = cols === 1 ? 0 : splitAt[cols][end]
    columns[cols - 1] = groups.slice(start, end)
    end = start
  }

  return columns
}

/**
 * Resolves the responsive column count for a given viewport width.
 *
 * @param {number} viewportWidth
 * @returns {number}
 */
export const resolveDirectoryColumnCount = (viewportWidth) => {
  if (viewportWidth >= 990) return 4
  if (viewportWidth >= 750) return 2
  return 1
}

const DEBOUNCE_MS = 120
const MAX_POPULAR_CARDS = 6

/** @type {WeakMap<HTMLElement, { refresh: () => void }>} */
const initializedSections = new WeakMap()

/**
 * @returns {boolean}
 */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * @param {HTMLElement} card
 * @param {boolean} isOpen
 */
export const setLetterCardOpen = (card, isOpen) => {
  card.setAttribute('data-letter-open', isOpen ? 'true' : 'false')
  const toggle = card.querySelector('[data-letter-toggle]')
  if (toggle instanceof HTMLElement) {
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
  }
}

/**
 * Initializes a V2 library section (`[data-library-v2]`).
 *
 * @param {HTMLElement} section
 */
export const initLibraryV2Section = (section) => {
  if (!(section instanceof HTMLElement)) return

  const existing = initializedSections.get(section)
  if (existing) {
    existing.refresh()
    return
  }

  const searchInput = section.querySelector(
    '[data-metaobject-grid-search-input]'
  )
  const directory = section.querySelector('[data-library-v2-directory]')
  const emptyState = section.querySelector(
    '[data-metaobject-grid-search-empty]'
  )
  const totalCountEl = section.querySelector('[data-library-v2-total-count]')
  const popularSection = section.querySelector('[data-library-popular-section]')
  const genericSection = section.querySelector('[data-library-generic-section]')

  if (!(directory instanceof HTMLElement)) return

  const singularUnit = section.dataset.countSingular || 'marque'
  const pluralUnit = section.dataset.countPlural || 'marques'
  const filterAttribute =
    section.getAttribute('data-metaobject-grid-search-filter-attribute') || ''

  const vehicleButtons = Array.from(
    section.querySelectorAll('[data-metaobject-grid-search-filter-button]')
  ).filter((btn) => btn instanceof HTMLButtonElement)

  const alphaButtons = Array.from(
    section.querySelectorAll('[data-library-alpha-btn]')
  ).filter((btn) => btn instanceof HTMLButtonElement)

  const popularCards = Array.from(
    section.querySelectorAll('[data-library-popular-card]')
  ).filter((card) => card instanceof HTMLElement)

  const genericCards = Array.from(
    section.querySelectorAll('[data-library-generic-card]')
  ).filter((card) => card instanceof HTMLElement)

  /** Master list of letter cards in A-Z order. */
  const allLetterCards = Array.from(
    directory.querySelectorAll('[data-letter-card]')
  )
    .filter((card) => card instanceof HTMLElement)
    .sort((a, b) =>
      (a.dataset.letterCard || '').localeCompare(b.dataset.letterCard || '')
    )

  let activeVehicle = ''
  if (filterAttribute && vehicleButtons.length > 0) {
    activeVehicle = normalizeForSearch(
      vehicleButtons
        .find((btn) => btn.getAttribute('aria-pressed') === 'true')
        ?.getAttribute('data-metaobject-grid-search-filter-value') ||
        section.getAttribute('data-metaobject-grid-search-default-filter') ||
        vehicleButtons[0]?.getAttribute(
          'data-metaobject-grid-search-filter-value'
        ) ||
        ''
    )
  }

  let activeLetter =
    alphaButtons
      .find((btn) => btn.classList.contains('is-active'))
      ?.getAttribute('data-library-alpha-btn') || 'A'

  let lastColumnCount = 0

  /**
   * @param {string} letter
   */
  const syncActiveAlphaButton = (letter) => {
    activeLetter = letter
    for (const btn of alphaButtons) {
      const btnLetter = btn.getAttribute('data-library-alpha-btn') || ''
      const isMatch = btnLetter === letter && !btn.disabled
      btn.classList.toggle('is-active', isMatch)
      if (isMatch) {
        btn.setAttribute('aria-current', 'true')
      } else {
        btn.removeAttribute('aria-current')
      }
    }
  }

  /**
   * Re-packs `allLetterCards` into responsive columns (4 on desktop, 2 on tablet,
   * 1 flat stack on mobile).
   *
   * @param {boolean} [force]
   */
  const packColumns = (force = false) => {
    const viewportWidth =
      typeof window !== 'undefined' && window.innerWidth > 0
        ? window.innerWidth
        : 1280
    const nextColCount = resolveDirectoryColumnCount(viewportWidth)

    if (!force && nextColCount === lastColumnCount) return
    lastColumnCount = nextColCount

    if (nextColCount <= 1) {
      directory.removeAttribute('data-columns-packed')
      directory.replaceChildren(...allLetterCards)
      return
    }

    const visibleGroups = []
    const hiddenCards = []

    for (const card of allLetterCards) {
      if (card.hidden) {
        hiddenCards.push(card)
      } else {
        const visibleRows = Number.parseInt(
          card.dataset.visibleRowCount || '1',
          10
        )
        visibleGroups.push({
          card,
          letter: card.dataset.letterCard || '',
          itemCount: Math.max(1, visibleRows)
        })
      }
    }

    const distributed = distributeLettersIntoColumns(
      visibleGroups,
      nextColCount
    )
    const columnNodes = distributed.map((colGroups, colIndex) => {
      const colEl = document.createElement('div')
      colEl.className = 'library-v2__column'
      colEl.setAttribute('data-library-v2-column', String(colIndex + 1))
      for (const group of colGroups) {
        colEl.appendChild(group.card)
      }
      return colEl
    })

    if (hiddenCards.length > 0 && columnNodes.length > 0) {
      const lastCol = columnNodes[columnNodes.length - 1]
      for (const hiddenCard of hiddenCards) {
        lastCol.appendChild(hiddenCard)
      }
    }

    directory.setAttribute('data-columns-packed', 'true')
    directory.replaceChildren(...columnNodes)
  }

  /**
   * Filters popular cards, generic cards, and directory brand rows.
   *
   * @param {{ resetMobileAccordion?: boolean }} [options]
   */
  const updateDirectory = ({ resetMobileAccordion = false } = {}) => {
    const normalizedQuery =
      searchInput instanceof HTMLInputElement
        ? normalizeForSearch(searchInput.value)
        : ''
    const isSearching = normalizedQuery.length > 0

    // 1. Generic codes callout banner (codes_defauts_marque)
    if (genericSection instanceof HTMLElement) {
      let visibleGenericCount = 0
      for (const card of genericCards) {
        const cardVehicle = normalizeForSearch(
          card.getAttribute('data-library-vehicle') || ''
        )
        const matchesVehicle =
          !filterAttribute ||
          activeVehicle.length === 0 ||
          cardVehicle.length === 0 ||
          cardVehicle === activeVehicle
        const shouldShow = !isSearching && matchesVehicle
        card.hidden = !shouldShow
        if (shouldShow) visibleGenericCount += 1
      }
      genericSection.hidden = visibleGenericCount === 0
    }

    // 2. Popular brands section (up to 6 visible per vehicle tab; hidden while searching)
    if (popularSection instanceof HTMLElement) {
      let visiblePopularCount = 0
      for (const card of popularCards) {
        const cardVehicle = normalizeForSearch(
          card.getAttribute('data-library-vehicle') || ''
        )
        const matchesVehicle =
          !filterAttribute ||
          activeVehicle.length === 0 ||
          cardVehicle.length === 0 ||
          cardVehicle === activeVehicle

        const shouldShow =
          !isSearching &&
          matchesVehicle &&
          visiblePopularCount < MAX_POPULAR_CARDS
        card.hidden = !shouldShow
        if (shouldShow) visiblePopularCount += 1
      }
      popularSection.hidden = visiblePopularCount === 0
    }

    // 3. Main A-Z directory
    let totalVisibleBrands = 0
    /** @type {Set<string>} */
    const availableLetters = new Set()
    let firstAvailableLetter = ''

    for (const card of allLetterCards) {
      const letter = card.dataset.letterCard || ''
      const rows = Array.from(
        card.querySelectorAll('[data-library-brand-row]')
      ).filter((row) => row instanceof HTMLElement)

      let letterVisibleCount = 0
      /** @type {HTMLElement | null} */
      let lastVisibleRow = null

      for (const row of rows) {
        row.classList.remove('is-last-visible')
        const haystack = normalizeForSearch(
          row.getAttribute('data-search-term') ||
            row.getAttribute('data-brand-title') ||
            row.textContent ||
            ''
        )
        const rowVehicle = normalizeForSearch(
          row.getAttribute('data-library-vehicle') || ''
        )

        const matchesQuery = !isSearching || haystack.includes(normalizedQuery)
        const matchesVehicle =
          !filterAttribute ||
          activeVehicle.length === 0 ||
          rowVehicle === activeVehicle

        const shouldShow = matchesQuery && matchesVehicle
        row.hidden = !shouldShow

        if (shouldShow) {
          letterVisibleCount += 1
          totalVisibleBrands += 1
          lastVisibleRow = row
        }
      }

      if (lastVisibleRow) {
        lastVisibleRow.classList.add('is-last-visible')
      }

      card.dataset.visibleRowCount = String(letterVisibleCount)
      card.hidden = letterVisibleCount === 0

      const countEl = card.querySelector('[data-letter-count]')
      if (countEl instanceof HTMLElement) {
        countEl.textContent = formatBrandCountLabel(
          letterVisibleCount,
          singularUnit,
          pluralUnit
        )
      }

      if (letterVisibleCount > 0) {
        availableLetters.add(letter)
        if (!firstAvailableLetter) {
          firstAvailableLetter = letter
        }
      }

      if (isSearching && letterVisibleCount > 0) {
        setLetterCardOpen(card, true)
      } else if (resetMobileAccordion) {
        setLetterCardOpen(card, letter === firstAvailableLetter)
      }
    }

    if (totalCountEl instanceof HTMLElement) {
      totalCountEl.textContent = formatBrandCountLabel(
        totalVisibleBrands,
        singularUnit,
        pluralUnit
      )
    }

    for (const btn of alphaButtons) {
      const btnLetter = btn.getAttribute('data-library-alpha-btn') || ''
      btn.disabled = !availableLetters.has(btnLetter)
    }

    const nextActiveLetter = availableLetters.has(activeLetter)
      ? activeLetter
      : firstAvailableLetter
    syncActiveAlphaButton(nextActiveLetter)

    if (emptyState instanceof HTMLElement) {
      emptyState.hidden = totalVisibleBrands > 0
    }
    directory.hidden = totalVisibleBrands === 0

    packColumns(true)
    section.dataset.metaobjectGridSearchReady = 'true'
  }

  // Bind vehicle filter tabs
  for (const btn of vehicleButtons) {
    btn.addEventListener('click', () => {
      const nextValue = normalizeForSearch(
        btn.getAttribute('data-metaobject-grid-search-filter-value') || ''
      )
      if (nextValue === activeVehicle) return

      activeVehicle = nextValue
      for (const tabBtn of vehicleButtons) {
        const tabValue = normalizeForSearch(
          tabBtn.getAttribute('data-metaobject-grid-search-filter-value') || ''
        )
        tabBtn.setAttribute(
          'aria-pressed',
          tabValue === activeVehicle ? 'true' : 'false'
        )
      }

      updateDirectory({ resetMobileAccordion: true })
    })
  }

  // Bind search input
  if (searchInput instanceof HTMLInputElement) {
    /** @type {number | undefined} */
    let debounceTimer
    const onSearchInput = () => {
      window.clearTimeout(debounceTimer)
      debounceTimer = window.setTimeout(() => {
        const isCleared = normalizeForSearch(searchInput.value).length === 0
        updateDirectory({ resetMobileAccordion: isCleared })
      }, DEBOUNCE_MS)
    }

    searchInput.addEventListener('input', onSearchInput)
    searchInput.addEventListener('search', onSearchInput)
  }

  // Bind A-Z alphabet buttons
  for (const btn of alphaButtons) {
    btn.addEventListener('click', () => {
      if (btn.disabled) return
      const letter = btn.getAttribute('data-library-alpha-btn') || ''
      if (!letter) return

      syncActiveAlphaButton(letter)
      const targetCard = allLetterCards.find(
        (card) => card.dataset.letterCard === letter && !card.hidden
      )
      if (!targetCard) return

      setLetterCardOpen(targetCard, true)
      if (typeof targetCard.scrollIntoView === 'function') {
        targetCard.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'start'
        })
      }
    })
  }

  // Bind mobile accordion headers
  for (const card of allLetterCards) {
    const toggle = card.querySelector('[data-letter-toggle]')
    if (!(toggle instanceof HTMLButtonElement)) continue

    toggle.addEventListener('click', () => {
      if (typeof window !== 'undefined' && window.innerWidth >= 750) return

      const isCurrentlyOpen = card.getAttribute('data-letter-open') === 'true'
      const nextOpen = !isCurrentlyOpen
      setLetterCardOpen(card, nextOpen)

      const letter = card.dataset.letterCard || ''
      if (nextOpen && letter) {
        syncActiveAlphaButton(letter)
      }
    })
  }

  // Responsive column re-pack on resize
  if (typeof window !== 'undefined') {
    window.addEventListener(
      'resize',
      () => {
        packColumns(false)
      },
      { passive: true }
    )
  }

  initializedSections.set(section, {
    refresh: () => updateDirectory({ resetMobileAccordion: false })
  })

  updateDirectory({ resetMobileAccordion: true })
}

/**
 * @param {ParentNode} [root]
 */
export const initLibraryV2Sections = (root = document) => {
  const sections = root.querySelectorAll('[data-library-v2]')
  for (const section of sections) {
    if (section instanceof HTMLElement) {
      initLibraryV2Section(section)
    }
  }
}

const bootLibraryV2 = () => {
  initLibraryV2Sections(document)
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootLibraryV2)
  } else {
    bootLibraryV2()
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target
    if (target instanceof HTMLElement) {
      initLibraryV2Sections(target)
    } else {
      initLibraryV2Sections(document)
    }
  })
}
